/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IAddressAddItem, IAddressAddUpdateProps } from '@msdyn365-commerce-modules/address';
import { IAddressList, IAddressListItem, IAddressListProps } from '@msdyn365-commerce-modules/address';
import { IAddressShowItem, IAddressShowProps } from '@msdyn365-commerce-modules/address';
import { IAccountManagementAddressViewProps } from '@msdyn365-commerce-modules/address';
import { convert_a2_to_a3 } from '../utils/convert_a2_a3';
import { cleanAddress } from '../../../common/utils/address';

const AddressShow: React.FC<IAddressShowProps> = ({ AddressDetail, items }) => {
    return (
        <Node {...AddressDetail}>
            {items &&
                items.map((item: IAddressShowItem) => {
                    return <>{cleanAddress(item.description)}</>;
                })}
        </Node>
    );
};

const AddressList: React.FC<IAddressList> = ({ List, isShowList, heading, items }) => {
    if (!isShowList) {
        return null;
    }

    return (
        <Node {...List}>
            {heading}
            {items &&
                items.map((item: IAddressListItem) => {
                    const { Item, key, error, showItems, isShowPrimaryButton, primaryButton, editButton, removeButton } = item;
                    const { Error, isShowError, title, message } = error;
                    return (
                        <Node {...Item} key={key}>
                            <AddressShow {...showItems} />
                            {isShowError && (
                                <Node {...Error}>
                                    {title}
                                    {message}
                                </Node>
                            )}
                            {isShowPrimaryButton && primaryButton}
                            {editButton}
                            {removeButton}
                        </Node>
                    );
                })}
        </Node>
    );
};

const AddressLists: React.FC<IAddressListProps> = ({
    ListAddress,
    heading,
    isShowEmptyListMessage,
    emptyListMessage,
    addButton,
    primaryAddressList,
    otherAddressList
}) => {
    return (
        <Node {...ListAddress}>
            {heading}
            {isShowEmptyListMessage && emptyListMessage}
            {addButton}
            <AddressList {...primaryAddressList} />
            <AddressList {...otherAddressList} />
        </Node>
    );
};
// tslint:disable-next-line:max-func-body-length
const AddressAddUpdate: React.FC<IAddressAddUpdateProps> = ({
    AddressForm,
    heading,
    items,
    hasError,
    error,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton,
    ...other
}) => {
    // @ts-ignore
    const resources = other?.moduleProps?.resources;
    // @ts-ignore
    const localeString = other?.moduleProps?.context?.request?.locale;
    // @ts-ignore
    const sitePath = other?.moduleProps?.context?.request?.sitePath;
    const europe = ['/fr', '/de'];
    // @ts-ignore
    const currency = other?.moduleProps?.context?.request?.channel?.Currency;
    const locale =
        sitePath && europe.includes(sitePath)
            ? 'eu'
            : currency === 'EUR' && localeString === 'en-GB'
            ? 'eu'
            : localeString.replace('-', '');

    const [hasAddressError, setHasAddressError] = React.useState(false);
    React.useEffect(() => {
        setHasAddressError(error.title ? true : false);
    }, [error.title]);
    // console.log(`${currency} ${localeString} ${locale}`);
    const countriesAvail = {
        enGB: {
            autocomplete: ['gb'],
            select: ['GBR']
        },
        it: {
            autocomplete: ['it'],
            select: ['ITA']
        },
        enUS: {
            autocomplete: ['us'],
            // autocomplete: ['us', 'ca'],
            // select: ['USA', 'CAN']
            select: ['USA']
        },
        eu: {
            // removed componentRestrictions for country as there is limit of 5 in google maps
            // autocomplete:['at','be','bg','ch','cz','de','dk','es','ee','fi','fr','gr','hr','hu','ie','li','lt','lu','lv','mc','nl','no','pl','pt','ro','sk','si','se'],
            autocomplete: [],
            select: [
                'AUT',
                'BEL',
                'BGR',
                'CHE',
                'CZE',
                'DEU',
                'DNK',
                'ESP',
                'EST',
                'FIN',
                'FRA',
                'GRC',
                'HRV',
                'HUN',
                'IRL',
                'LIE',
                'LTU',
                'LUX',
                'LVA',
                'MCO',
                'NLD',
                'NOR',
                'POL',
                'PRT',
                'ROU',
                'SVK',
                'SVN',
                'SWE'
            ]
        },
        enSG: {
            autocomplete: ['sg'],
            select: ['SGP']
        },
        enHK: {
            autocomplete: ['hk'],
            select: ['HKG']
        },
        zhHK: {
            autocomplete: ['hk'],
            select: ['HKG']
        }
    };
    // @ts-ignore
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
    // tslint:disable-next-line:typedef
    function gMapsAutocomplete() {
        const googleComponents = [
            { googleComponent: 'sublocality_level_1', id: 'shipping_addresscity' },
            { googleComponent: 'locality', id: 'shipping_addresscity' },
            { googleComponent: 'postal_town', id: 'shipping_addresscity' },
            { googleComponent: 'administrative_area_level_3', id: 'shipping_addresscity' },
            { googleComponent: 'administrative_area_level_1', id: 'shipping_addressstate' },
            { googleComponent: 'postal_code_prefix', id: 'shipping_addresszipcode' },
            { googleComponent: 'postal_code', id: 'shipping_addresszipcode' },
            { googleComponent: 'country', id: 'shipping_addressthreeletterisoregionname' },
            { googleComponent: 'administrative_area_level_2', id: 'shipping_addresscounty' }
        ];

        const autocompleteFormField = document.getElementById('shipping_addressstreet');
        // @ts-ignore
        const autocomplete = new window.google.maps.places.Autocomplete(autocompleteFormField, {
            types: ['address'],
            componentRestrictions: { country: countriesAvail[locale] && countriesAvail[locale].autocomplete }
        });
        if (autocompleteFormField === null) {
            return;
        }
        autocompleteFormField.setAttribute('placeholder', resources.startTypingYourAddress);
        // @ts-ignore
        window.google.maps.event.clearInstanceListeners(autocompleteFormField);
        // @ts-ignore
        window.google.maps.event.addListener(autocomplete, `place_changed`, () => {
            const place = autocomplete.getPlace();
            // console.log(place);
            if (nativeInputValueSetter === undefined) {
                return;
            }
            nativeInputValueSetter.call(autocompleteFormField, place.name);
            const eCustom = new Event('change', { bubbles: true });
            autocompleteFormField.dispatchEvent(eCustom);
            for (const googleComponent of googleComponents) {
                const addressComponents = place.address_components;
                if (addressComponents && addressComponents.length > 0) {
                    // @ts-ignore
                    addressComponents.forEach(addressComponent => populateFormElements(addressComponent, googleComponent));
                }
            }
        });
    }
    // tslint:disable-next-line:typedef no-any
    function populateFormElements(
        addressComponent: { types: any[]; short_name: string; long_name: string },
        formMap: { googleComponent: any; id: string }
    ) {
        const addressType = addressComponent.types[0];

        if (document.getElementById(formMap.id) === undefined || document.getElementById(formMap.id) === null) {
            return;
        }

        if (formMap.googleComponent === addressType) {
            if (formMap.id === 'shipping_addressthreeletterisoregionname' || formMap.id === 'shipping_addressstate') {
                const input = document.getElementById(formMap.id) as HTMLSelectElement;
                if (input !== null) {
                    const formValue = locale === 'it' && formMap.id === 'shipping_addressstate' ? 'DEF' : addressComponent.short_name;
                    // console.log(formValue);
                    const selectedValue = selectItemByValue(input, formValue, addressType === 'country' ? true : false);
                    // console.log(selectedValue);
                    if (selectedValue !== undefined) {
                        input.value = selectedValue;
                        // console.log(input.value);
                        const event = new Event('change', { bubbles: true });
                        input.dispatchEvent(event);
                    }
                }
            } else {
                const formValue = formMap.id === 'shipping_addresscounty' ? addressComponent.short_name : addressComponent.long_name;
                const input = document.getElementById(formMap.id);
                if (input !== undefined) {
                    if (nativeInputValueSetter === undefined) {
                        return;
                    }
                    nativeInputValueSetter.call(input, formValue);
                    const eCustom = new Event('input', { bubbles: true });
                    if (input !== null) {
                        input.dispatchEvent(eCustom);
                    }
                }
            }
        }
    }
    // @ts-ignore
    // tslint:disable-next-line:typedef
    function selectItemByValue(elmnt: HTMLSelectElement, value: string, is_convert_a2_to_a3) {
        for (let i = 0; i < elmnt.options.length; i++) {
            const eValue = elmnt.options[i].value;
            if ((is_convert_a2_to_a3 && eValue === convert_a2_to_a3[value]) || (!is_convert_a2_to_a3 && eValue === value)) {
                return elmnt.options[i].value;
            }
        }
    }
    const checkPhone = () => {
        const saveAddressButton = document.querySelector('.msc-address-form__button-save.msc-btn') as HTMLButtonElement;
        const phone = document.getElementById('shipping_addressphone') as HTMLInputElement;
        if (phone) {
            const phoneCtn = phone.closest('.msc-address-form__item') as HTMLElement;
            const phoneAlert = phoneCtn.getElementsByClassName('msc-address-form__alert')[0];
            const pattern = /^[0-9]*$/;
            const pattern2 = /^\+?\d+$/;
            phone.setAttribute('aria-required', 'true');
            phone.setAttribute('type', 'tel');
            phone.setAttribute('maxlength', '15');
            if (phone.value.length === 0) {
                saveAddressButton.setAttribute('disabled', 'true');
            }
            phone.onkeyup = () => {
                if (!phone.value.match(pattern) && !phone.value.match(pattern2)) {
                    phoneCtn.classList.add('address-form__item-invalid');
                    phoneAlert.textContent = resources.addressPhoneRequiredErrorMessage;
                    saveAddressButton.setAttribute('disabled', 'true');
                } else if (phone.value.length < 6) {
                    phoneCtn.classList.add('address-form__item-invalid');
                    phoneAlert.textContent = resources.addressPhoneRequiredErrorMessage;
                    saveAddressButton.setAttribute('disabled', 'true');
                } else {
                    phoneCtn.classList.remove('address-form__item-invalid');
                    phoneAlert.textContent = '';
                    saveAddressButton.removeAttribute('disabled');
                }
            };
        }
    };
    function phoneValidation(): void {
        const addressForm = document.getElementsByClassName('msc-address-form')[0];
        const saveAddressButton = document.querySelector('.msc-address-form__button-save.msc-btn') as HTMLButtonElement;
        const county = document.getElementById('shipping_addresscounty') as HTMLInputElement;
        const countyCtn = county && county !== null ? (county.closest('.msc-address-form__item') as HTMLInputElement) : undefined;
        const countyAlert = countyCtn?.getElementsByClassName('msc-address-form__alert')[0];

        if (countyCtn !== undefined) {
            countyCtn.onkeyup = () => {
                countyCtn?.classList.remove('address-form__item-invalid');
                if (countyAlert) {
                    countyAlert.textContent = '';
                }
            };
        }
        if (saveAddressButton !== undefined) {
            saveAddressButton.onclick = e => {
                const countyValid =
                    locale === 'it' && (county.value === '' || county.value.length > 2 || !/[^a-z]/i.test(county.value) === false)
                        ? false
                        : true;
                if (!countyValid) {
                    countyCtn?.classList.add('address-form__item-invalid');
                    if (countyAlert) {
                        countyAlert.textContent = resources.countryErrorMessage;
                    }
                    const elements = addressForm.querySelectorAll('input');
                    let fieldInALL = true;
                    elements.forEach(fieldInControl);
                    function fieldInControl(element: HTMLInputElement): void {
                        if (element.value === '' && element.name !== 'County') {
                            fieldInALL = false;
                        }
                    }
                    if (fieldInALL) {
                        e.stopPropagation();
                    }
                }
            };
        }
    }
    React.useEffect(() => {
        gMapsAutocomplete();
        const options = document.querySelectorAll('#shipping_addressthreeletterisoregionname option');
        options.forEach(o => {
            const optionV = (o as HTMLOptionElement).value;
            if (countriesAvail[locale] && !countriesAvail[locale].select.includes(optionV)) {
                o.remove();
            }
        });
        const countrySelect = document.getElementById('shipping_addressthreeletterisoregionname') as HTMLSelectElement;
        if (locale === 'enUS') {
            countrySelect.value = 'USA';
        }
        const event = new Event('change', { bubbles: true });
        countrySelect.dispatchEvent(event);
    }, []);

    // Auto select of DEF value in state select on italian site
    React.useEffect(() => {
        const event = new Event('change', { bubbles: true });
        if (locale === 'it' && (document.querySelector('#shipping_addressstate option[value=DEF]') as HTMLOptionElement)) {
            const optionDEF = document.querySelector('#shipping_addressstate option[value=DEF]') as HTMLOptionElement;
            optionDEF.setAttribute('aria-selected', 'true');
            (optionDEF.parentNode as HTMLSelectElement).value = 'DEF';
            (optionDEF.parentNode as HTMLSelectElement).dispatchEvent(event);
            (optionDEF.parentNode as HTMLSelectElement).disabled = Boolean('true');
        }
    }, [document.querySelector('#shipping_addressstate option[value=DEF]')]);

    React.useEffect(() => {
        const phoneItem = items.find(i => i.key === 'Phone');

        //@ts-ignore
        if (phoneItem?.input?.props?.onChange) {
            //@ts-ignore
            const { onChange } = phoneItem.input.props;

            const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                onChange(event);
                checkPhone();
            };

            //@ts-ignore
            phoneItem.input.props.onChange = handlePhoneChange;
        }
    }, [items]);

    React.useEffect(() => {
        phoneValidation();
    }, [document.getElementsByClassName('msc-address-form')[0]]);

    // Refesh once saving processed
    let updatedCount = 0;
    React.useEffect(() => {
        ++updatedCount;
        if (updatedCount > 1) {
            location.reload();
        }
    }, [document.querySelector('.msc-address-form__button-updating')]);
    return (
        <Node {...AddressForm}>
            {heading}
            {items &&
                items.map((item: IAddressAddItem) => {
                    const { AddressItem, key, label, alert, input } = item;
                    if (key === 'Name' && input) {
                        // @ts-ignore
                        input.props.maxLength = 34;
                    }
                    return (
                        <Node {...AddressItem} key={key}>
                            {label}
                            {alert}
                            {input}
                        </Node>
                    );
                })}
            {hasAddressError && (
                <Node {...error.AddressError}>
                    <p className='msc-address-form__error-message'>{resources.addressErrorMessage}</p>
                </Node>
            )}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

const AccountManagementAddressView: React.FC<IAccountManagementAddressViewProps> = props => {
    const { AccountAddressManagement, viewState, showAddressList, showAddOrUpdateAddress } = props;

    return (
        <Module {...AccountAddressManagement}>
            {viewState.isShowAddresList && <AddressLists {...showAddressList} />}
            {viewState.isShowAddOrUpdateAddress && <AddressAddUpdate {...showAddOrUpdateAddress} {...AccountAddressManagement} />}
        </Module>
    );
};

export default AccountManagementAddressView;
